// Natal chart: everything the Chart and Today screens read, computed once
// from birth data. Deterministic; see core.js.

import {
  SIGNS, PLANETS, PERSONAL, mod360, signOf, signIndexOf, formatLon,
  bodyLongitude, bodySpeed, trueNodeLongitude, birthDataToUtc, localToUtc,
  angles, wholeSignCusps, houseOf, dignityOf, findAspects, lunationPhase, signedDelta,
} from "./core.js";
import { computeCusps } from "./houses.js";

export const CHART_VERSION = 2;

function point(kind, lon, speed = 0, extra = {}) {
  const s = signOf(lon);
  const f = formatLon(lon);
  return {
    kind, lon: +lon.toFixed(4), speed: +speed.toFixed(4),
    sign: s.name, signKey: s.key, signIndex: signIndexOf(lon),
    degree: +(mod360(lon) - signIndexOf(lon) * 30).toFixed(2),
    text: f.text,
    retrograde: speed < 0,
    ...extra,
  };
}

/**
 * Without a birth time a body can change sign during the birth day (the
 * Moon on ~40% of days, the Sun on ~3%). Returns the two signs and the local
 * clock time of the change, or null if it stayed in one sign all day.
 */
function dayRange(body, birthData) {
  const { birthDate, birthLocation } = birthData;
  const tz = birthLocation?.timezone || "UTC";
  const start = localToUtc({ ...birthDate, hour: 0, minute: 0 }, tz);
  const end = localToUtc({ ...birthDate, hour: 23, minute: 59 }, tz);
  const a = signOf(bodyLongitude(body, start));
  const b = signOf(bodyLongitude(body, end));
  if (a.key === b.key) return null;
  // Bisect for the ingress moment.
  let lo = start.getTime(), hi = end.getTime();
  while (hi - lo > 30000) {
    const mid = (lo + hi) / 2;
    if (signOf(bodyLongitude(body, new Date(mid))).key === a.key) lo = mid; else hi = mid;
  }
  let at = null;
  try {
    at = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(new Date(hi));
  } catch (e) { /* zone unavailable */ }
  return { from: a.name, to: b.name, at };
}

const nodeSpeed = (utc) => {
  const before = trueNodeLongitude(new Date(utc.getTime() - 43200000));
  const after = trueNodeLongitude(new Date(utc.getTime() + 43200000));
  let d = mod360(after - before);
  if (d > 180) d -= 360;
  return d;
};

/** @param birthData app shape: { birthDate, hasBirthTime, birthTime, birthLocation:{lat,lng,timezone,city} } */
export function computeNatal(birthData, { houseSystem = "placidus" } = {}) {
  const utc = birthDataToUtc(birthData);
  const { hasBirthTime, birthLocation } = birthData;

  const points = {};
  for (const key of PLANETS) {
    points[key] = point("planet", bodyLongitude(key, utc), bodySpeed(key, utc));
  }
  const node = trueNodeLongitude(utc);
  const nSpeed = nodeSpeed(utc);
  points.northNode = point("node", node, nSpeed);
  points.northNode.retrograde = false; // the node's backward drift is its normal motion
  const southNode = point("node", mod360(node + 180), nSpeed);
  southNode.retrograde = false;

  let houses = null;
  let ang = null;
  if (hasBirthTime && birthLocation?.lat != null) {
    ang = angles(utc, birthLocation.lat, birthLocation.lng);
    points.asc = point("angle", ang.asc);
    // Within 1° of a sign edge, a few minutes of birth time changes the Rising sign.
    points.asc.nearCusp = points.asc.degree < 1 || points.asc.degree > 29;
    points.mc = point("angle", ang.mc);
    const cusps = computeCusps(houseSystem, ang, birthLocation.lat);
    houses = {
      system: cusps.system,
      requested: houseSystem,
      cusps: cusps.cusps.map((c) => +c.toFixed(4)),
      wholeSign: wholeSignCusps(ang.asc),
    };
    for (const key of Object.keys(points)) {
      if (key === "asc" || key === "mc") continue;
      points[key].house = houseOf(points[key].lon, houses.cusps);
      points[key].wholeSignHouse = houseOf(points[key].lon, houses.wholeSign);
    }
    southNode.house = houseOf(southNode.lon, houses.cusps);
  }

  // Outer-planet dignities are generational (and the scheme has no outer
  // exaltations), so they aren't shown or scored.
  for (const key of PLANETS) points[key].dignity = ["uranus", "neptune", "pluto"].includes(key) ? null : dignityOf(key, points[key].signKey);

  // Sect: a day chart has the Sun above the horizon (houses 7 to 12).
  const sect = ang ? (mod360(points.sun.lon - ang.asc) >= 180 ? "day" : "night") : null;

  let fortune = null;
  if (ang) {
    const lon = sect === "day"
      ? ang.asc + points.moon.lon - points.sun.lon
      : ang.asc + points.sun.lon - points.moon.lon;
    fortune = point("lot", mod360(lon));
    fortune.house = houseOf(fortune.lon, houses.cusps);
  }

  let aspects = findAspects(points);
  let lunationRange = null;
  if (!hasBirthTime) {
    // The Moon moves ~13° a day. Keep a Moon aspect only if it holds for the
    // whole birth day; flag the birth Moon phase if it changed that day.
    const tz = birthLocation?.timezone || "UTC";
    const dayStart = localToUtc({ ...birthData.birthDate, hour: 0, minute: 0 }, tz);
    const dayEnd = localToUtc({ ...birthData.birthDate, hour: 23, minute: 59 }, tz);
    const moonAt = (t) => ({ ...points, moon: { ...points.moon, lon: bodyLongitude("moon", t), speed: points.moon.speed } });
    const holds = (t) => new Set(findAspects(moonAt(t)).filter((a) => a.a === "moon" || a.b === "moon").map((a) => `${a.a}|${a.b}|${a.type}`));
    const atStart = holds(dayStart), atEnd = holds(dayEnd);
    aspects = aspects.filter((a) => (a.a !== "moon" && a.b !== "moon") || (atStart.has(`${a.a}|${a.b}|${a.type}`) && atEnd.has(`${a.a}|${a.b}|${a.type}`)))
      .map((a) => (a.a === "moon" || a.b === "moon" ? { ...a, timeSensitive: true } : a));
    const l0 = lunationPhase(bodyLongitude("sun", dayStart), bodyLongitude("moon", dayStart));
    const l1 = lunationPhase(bodyLongitude("sun", dayEnd), bodyLongitude("moon", dayEnd));
    if (l0.key !== l1.key) lunationRange = { from: l0, to: l1 };
  }

  // Element and modality balance, weighted toward the placements that
  // carry the most of a person: luminaries and Ascendant first.
  const WEIGHT = { sun: 3, moon: 3, asc: 3, mercury: 2, venus: 2, mars: 2, jupiter: 1, saturn: 1, uranus: 0.5, neptune: 0.5, pluto: 0.5 };
  const elements = { fire: 0, earth: 0, air: 0, water: 0 };
  const modalities = { cardinal: 0, fixed: 0, mutable: 0 };
  for (const [key, w] of Object.entries(WEIGHT)) {
    const p = points[key];
    if (!p) continue;
    const s = SIGNS[p.signIndex];
    elements[s.element] += w;
    modalities[s.modality] += w;
  }
  const rank = (o) => Object.entries(o).sort((a, b) => b[1] - a[1]);
  const totalW = Object.values(elements).reduce((a, b) => a + b, 0);
  // Ties go to the Sun's element, then the Moon's.
  const tieOrder = [SIGNS[points.sun.signIndex].element, SIGNS[points.moon.signIndex].element];
  const elRank = Object.entries(elements).sort((a, b) => (b[1] - a[1]) || (tieOrder.indexOf(b[0]) - tieOrder.indexOf(a[0])));
  const balance = {
    elements, modalities,
    dominantElement: elRank[0][0],
    dominantShare: +(elRank[0][1] / totalW).toFixed(3),
    lackingElements: Object.entries(elements).filter(([, v]) => v / totalW < 0.08).map(([k]) => k),
    dominantModality: rank(modalities)[0][0],
  };

  // Chart ruler: traditional ruler of the rising sign.
  let chartRuler = null;
  if (points.asc) {
    const s = SIGNS[points.asc.signIndex];
    chartRuler = { planet: s.ruler, modern: s.modernRuler, ...pick(points[s.ruler]) };
    // Combust: within 8.5° of the Sun; cazimi: within 17' (traditional limits).
    if (s.ruler !== "sun") {
      const gap = Math.abs(signedDelta(points.sun.lon, points[s.ruler].lon));
      chartRuler.sunGap = +gap.toFixed(2);
      chartRuler.cazimi = gap <= 17 / 60;
      chartRuler.combust = !chartRuler.cazimi && gap < 8.5;
    }
  }

  // Stelliums: three or more of the ten planets sharing a sign (or house).
  const bySign = {};
  const byHouse = {};
  for (const key of PLANETS) {
    (bySign[points[key].sign] = bySign[points[key].sign] || []).push(key);
    if (points[key].house) (byHouse[points[key].house] = byHouse[points[key].house] || []).push(key);
  }
  // Only count groups with at least two personal planets; three slow
  // planets together is a generational signature, not a personal one.
  const personalIn = (planets) => planets.filter((k) => PERSONAL.includes(k)).length;
  const stelliums = [
    ...Object.entries(bySign).filter(([, v]) => v.length >= 3 && personalIn(v) >= 2).map(([sign, planets]) => ({ by: "sign", sign, planets, includesSun: planets.includes("sun") })),
    ...Object.entries(byHouse).filter(([, v]) => v.length >= 3 && personalIn(v) >= 2).map(([house, planets]) => ({ by: "house", house: +house, planets, includesSun: planets.includes("sun") })),
  ];

  // Dominant planets: a simple, explainable tally.
  const score = {};
  for (const key of PLANETS) {
    let s = 0;
    const p = points[key];
    // Outer-planet "dignities" are generational (Pluto in Scorpio, 1983-95), so they don't count here.
    if (!["uranus", "neptune", "pluto"].includes(key) && (p.dignity === "domicile" || p.dignity === "exaltation")) s += 2;
    if (p.house && [1, 4, 7, 10].includes(p.house)) s += 2;
    if (chartRuler?.planet === key) s += 3;
    if (SIGNS[points.sun.signIndex].ruler === key) s += 1;
    if (SIGNS[points.moon.signIndex].ruler === key) s += 1;
    for (const a of aspects) {
      if (a.generational || (a.a !== key && a.b !== key)) continue;
      const other = a.a === key ? a.b : a.a;
      s += a.major ? 0.5 : 0.25;
      if (a.type === "conjunction" && ["sun", "moon", "asc", "mc"].includes(other)) s += 1.5;
    }
    score[key] = s;
  }
  const dominantPlanets = rank(score).slice(0, 3).map(([k, v]) => ({ planet: k, score: +v.toFixed(2) }));

  return {
    version: CHART_VERSION,
    utc: utc.toISOString(),
    hasBirthTime: !!hasBirthTime,
    points,
    southNode,
    houses,
    sect,
    fortune,
    aspects,
    balance,
    chartRuler,
    stelliums,
    dominantPlanets,
    lunation: { ...lunationPhase(points.sun.lon, points.moon.lon), exact: hasBirthTime ? nearLunation(points) : null },
    lunationRange,
    moonRange: hasBirthTime ? null : dayRange("moon", birthData),
    sunRange: hasBirthTime ? null : dayRange("sun", birthData),
    personal: PERSONAL,
  };
}

/** Hours between birth and the nearest exact new or full Moon, if it's
    within 12 hours. Negative hours: the lunation came after the birth. */
function nearLunation(points) {
  const elong = mod360(points.moon.lon - points.sun.lon);
  const rate = (points.moon.speed - points.sun.speed) / 24; // degrees per hour
  if (!(rate > 0)) return null;
  for (const [kind, at] of [["new", 0], ["full", 180], ["new", 360]]) {
    const hours = (elong - at) / rate;
    if (Math.abs(hours) <= 12) return { kind, hours: +hours.toFixed(1) };
  }
  return null;
}

function pick(p) {
  if (!p) return {};
  return { sign: p.sign, house: p.house || null, text: p.text, dignity: p.dignity || null, retrograde: p.retrograde };
}

/**
 * Legacy shape consumed by older screens (ChartDetail, synastry, tags):
 * { hasBirthTime, placements, houses:[{house,sign,degree}], aspects, seventhHouse, longitudes, ascDeg }
 */
export function toLegacyChart(natal) {
  const P = natal.points;
  const pl = (p) => p && { sign: p.sign, degree: +p.degree.toFixed(1), house: p.house || null, retrograde: !!p.retrograde };
  const placements = {
    sun: pl(P.sun), moon: pl(P.moon),
    rising: P.asc ? { sign: P.asc.sign, degree: +P.asc.degree.toFixed(1) } : null,
    mercury: pl(P.mercury), venus: pl(P.venus), mars: pl(P.mars),
    jupiter: pl(P.jupiter), saturn: pl(P.saturn), uranus: pl(P.uranus),
    neptune: pl(P.neptune), pluto: pl(P.pluto), northNode: pl(P.northNode),
    descendant: P.asc ? (() => { const f = formatLon(P.asc.lon + 180); return { sign: f.sign, degree: +(f.deg + f.min / 60).toFixed(1) }; })() : null,
    midheaven: P.mc ? { sign: P.mc.sign, degree: +P.mc.degree.toFixed(1) } : null,
  };
  const houses = natal.houses
    ? natal.houses.cusps.map((c, i) => { const f = formatLon(c); return { house: i + 1, sign: f.sign, degree: +(f.deg + f.min / 60).toFixed(1) }; })
    : null;
  const longitudes = Object.fromEntries(Object.entries(P).filter(([k]) => k !== "asc" && k !== "mc").map(([k, v]) => [k, v.lon]));
  const legacyAspects = natal.aspects
    .filter((a) => P[a.a]?.kind === "planet" && P[a.b]?.kind === "planet" && a.major)
    .map((a) => ({ planet1: a.a, planet2: a.b, type: a.type, orb: +a.orb.toFixed(1) }));
  let seventhHouse = null;
  if (houses) {
    const sign = houses[6].sign;
    const ruler = SIGNS.find((s) => s.name === sign).ruler;
    seventhHouse = { sign, ruler, rulerSign: P[ruler]?.sign || null, rulerHouse: P[ruler]?.house || null, planetsInHouse: [] };
  }
  return {
    hasBirthTime: natal.hasBirthTime,
    placements, houses, aspects: legacyAspects, seventhHouse,
    longitudes, ascDeg: P.asc ? P.asc.lon : null,
    natal,
  };
}
